import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, throwError } from 'rxjs';
import { AUTH_SESSION_PORT } from '../ports/auth-session.port';
import { ApiError } from '../entities/api-error.entity';

const PUBLIC_AUTH_PATHS = [
  '/auth/login',
  '/auth/public-key',
  '/auth/recover-password',
  '/auth/reset-password',
];

const isPublicAuthRoute = (url: string): boolean =>
  PUBLIC_AUTH_PATHS.some((path) => url.includes(path));

const isLogoutRoute = (url: string): boolean =>
  url.includes('/auth/logout');

const isUnauthorized = (error: unknown): boolean => {
  if (error instanceof HttpErrorResponse) return error.status === 401;
  if (isApiError(error)) return error.originalError?.status === 401;
  return false;
};

const isApiError = (error: unknown): error is ApiError =>
  typeof error === 'object' &&
  error !== null &&
  'originalError' in error &&
  'generalErrorMessage' in error;

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const session = inject(AUTH_SESSION_PORT);
  const router = inject(Router);

  const isPublicAuth = isPublicAuthRoute(req.url);
  const token = session.getAccessToken();

  const authorizedReq =
    token && !isPublicAuth
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

  return next(authorizedReq).pipe(
    catchError((error) => {
      // No hacer logout+redirect si el 401 viene de rutas públicas de auth
      // (ej: credenciales inválidas en el login — el componente lo maneja)
      // Tampoco si el 401 viene del MISMO logout (prevenir loop infinito)
      if (isUnauthorized(error) && !isPublicAuth && !isLogoutRoute(req.url)) {
        session
          .logout()
          .pipe(finalize(() => router.navigate(['/auth/login'])))
          .subscribe();
      } else if (isUnauthorized(error) && isLogoutRoute(req.url)) {
        // Si el logout falla con 401, solo redirige sin reintentarlo
        router.navigate(['/auth/login']);
      }
      return throwError(() => error);
    }),
  );
};
